// Brian Zengel's PR #885 review #3: removed unused imports of AgGridModule,
// GridApi, GridOptions, IServerSideDatasource. The clientSide-mode wiring
// these supported was abandoned during the SSRM migration but the imports
// + unused fields remained vestigial.
import {
  CellClickedEvent,
  CellValueChangedEvent,
} from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import {
  columnLookupToColDefs,
  DEVICE_SEARCH_COLUMN_LOOKUP,
  DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER,
  DEVICE_SEARCH_SORT_TIEBREAKER,
} from '@cdw-ae/cdw-grid-configs';
import {
  AccessManagementDesignationRecord,
  COMMON_DEPROVISION_REASONS,
  DateTimeSettings,
  DeviceAsset,
  DeviceDiskUsed,
  GoogleDeviceStatus,
  type JobExecutionStatus,
} from '@cdw-ae/common-data';
import {
  MatDialog,
  MaterialLibraryModule,
} from '@cdw-ae/common-frontend-materials';
import { FieldUpdateConfig } from '@cdw-ae/common-ui-components-bulk-field-update';
import {
  BULK_ACTION_ELIGIBILITY_API,
  BULK_ACTION_EXECUTOR,
  BULK_ACTION_LABELS_PROVIDER,
  type BulkActionEligibilityApi,
  type BulkActionExecutionRequest,
  type BulkActionExecutor,
  type BulkActionSkipGroup,
  type BulkActionAnalyticsMetadata as LibraryBulkActionAnalyticsMetadata,
  CdwBulkUpdateActionComponent,
  CommonUiComponentsBulkUpdateActionModule,
} from '@cdw-ae/common-ui-components-bulk-update-action';
import {
  CdwConfirmationDialogComponent,
  type CdwConfirmationDialogData,
  CdwDialogModule,
} from '@cdw-ae/common-ui-components-dialog';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN,
  CdwColDef,
  CdwSearchGridStateService,
  CdwSsrmGridComponent,
  previewColumn,
  TreePickerCellEditorComponent,
} from '@cdw-ae/common-ui-components-search-grid';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import {
  buildSsrmLockStorageKey,
  bulkJobEnqueued,
  cancelJobRequested,
  JobTypes,
  lockHydratedFromStorage,
  selectIsFullAssetSyncRunning,
  selectIsLockActiveForGridSession,
  selectStuckJobsForGridSession,
} from '@cdw-ae/jobs-status-state';
import { SnackbarService } from '@cdw-ae/snackbar';
import { FilterBarParamStrategy } from '@cdw-ae/url-params';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  bufferTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pairwise,
  Subject,
  take,
  takeUntil,
  TimeoutError,
} from 'rxjs';
import {
  GoogleTagManagerService,
  type BulkActionType,
} from '../../../../common/services/analytics/google-tag-manager.service';
import * as FilterActions from '../../../../+state/filter/filter.actions';
import { selectSelectedFilters } from '../../../../+state/filter/filter.selectors';
import {
  selectCustomerDateTimeSettings,
  selectCustomerId,
  selectDbUserId,
  selectUserEmail,
  selectFbUserId,
  selectUserName,
} from '../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../common/constants/tailwind-colors';
import { ComponentStateService } from '../../../../common/services/component-state/component-state.service';
import { DeviceActionLabelsProvider } from '../../../../common/services/device-action-eligibility/device-action-labels.provider';
import { DeviceExportImportService } from '../../../../common/services/device/device-export-import.service';
import { DevicesDataService } from '../../../../common/services/device/devices-data.service';
import { JobMonitorService } from '../../../../common/services/job-monitor/job-monitor.service';
import { DeviceSearchFilterSettings } from '../../../../common/services/route-config/configs/filter-bar-config.config';
import {
  FilterBarId,
  FILTER_QUERY_PARAM_PREFIXES,
} from '../../../../common/services/route-config/models/route-config.model';
import { OrgUnitsDataService } from '../administration/access-management/org-units/org-units-data.service';
import { NavbarButtonsRegistryService } from '../navbar-action-buttons/services/navbar-buttons-registry.service';
import {
  buildBulkEditFieldConfigs,
  CROSS_PAGE_REMINDER_ACTIONS,
  CROSS_PAGE_REMINDER_TEXT,
  DeviceAction,
  getBulkEditConfigForAction,
} from './configs/bulk-edit-config';
import { DEVICE_SEARCH_FILTER_STORAGE_KEY } from './device-search-filter-storage.constants';
import { StuckJobBannerComponent } from './stuck-job-banner/stuck-job-banner.component';

@Component({
  selector: 'device-search',
  standalone: true,
  imports: [
    CommonModule,
    // AgGridModule removed (Brian's #3 — vestigial since SSRM migration)
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MaterialLibraryModule,
    CdwDialogModule,
    CdwIconModule,
    CommonUiComponentsBulkUpdateActionModule,
    CdwSsrmGridComponent,
    FilterBarTextMatchersModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
    StuckJobBannerComponent,
  ],
  providers: [
    {
      provide: CDW_SAVE_COMPONENT_STATE_SERVICE_TOKEN,
      useClass: ComponentStateService,
    },
    {
      provide: BULK_ACTION_ELIGIBILITY_API,
      useFactory: (
        devicesDataService: DevicesDataService,
      ): BulkActionEligibilityApi => ({
        evaluate: (actionType, gridSessionId, opts) =>
          devicesDataService
            .evaluateSelectionEligibility(actionType, gridSessionId, opts)
            .pipe(
              map((dto) => ({
                qualifiedIds: dto.qualifiedIds,
                skipGroups: dto.skipGroups.map((g) => ({
                  reason: g.reason,
                  label: g.label,
                  count: g.count,
                  items: (g.deviceIds ?? []).map((id) => ({ id }) as DeviceAsset),
                })),
                totals: {
                  selected: dto.totalSelected,
                  qualified: dto.qualified,
                  skipped: dto.skipped,
                },
              })),
            ),
      }),
      deps: [DevicesDataService],
    },
    {
      provide: BULK_ACTION_LABELS_PROVIDER,
      useExisting: DeviceActionLabelsProvider,
    },
    {
      provide: BULK_ACTION_EXECUTOR,
      useFactory: (
        devicesDataService: DevicesDataService
      ): BulkActionExecutor => {
        // Phase 10 of EDUS-2486: branches on whether the request carries
        // explicit per-row `updates` (legacy clientside path — kept for
        // legacy grids that still send arrays) OR `updateSpec` +
        // `useSelection` (new server-side selection store path — kills
        // L4: 30 MB POST body collapses to ~200 bytes regardless of size).
        //
        // The grid session UUID is read from `localStorage` (the
        // SsrmGridComponent.mintOrRestoreSessionId persists it as
        // `grid-session:device-search`). Reading it here at execute()
        // time ensures we always pick up the live value even if the
        // grid component hasn't been view-init'd yet.
        const readGridSessionId = (): string => {
          try {
            return sessionStorage.getItem('grid-session:device-search') ?? '';
          } catch {
            return '';
          }
        };
        return {
          execute: (req: BulkActionExecutionRequest) => {
            // Phase 9 of EDUS-2485 left selectedDeviceIds empty (per-row
            // selection state is server-authoritative now), so the
            // drawer's per-row `updates` array is also empty. When that's
            // the case AND the drawer supplied an updateSpec (Phase 10
            // of EDUS-2486), switch to the server-side selection path:
            // the backend resolves the affected ids from Redis instead
            // of trusting a client-supplied 30 MB array.
            const hasUpdates = req.updates && req.updates.length > 0;
            if (!hasUpdates && req.updateSpec) {
              return devicesDataService.updateDevicesUsingSelection(
                req.updateSpec,
                req.jobType,
                readGridSessionId()
              );
            }
            // Legacy path: per-row updates. Defensive coalesce kept from
            // Phase 0 since `updates` is now optional on the wire shape.
            return devicesDataService.updateDevices(
              req.updates ?? [],
              req.jobType
            );
          },
        };
      },
      deps: [DevicesDataService],
    },
  ],
  templateUrl: './device-search.component.html',
  styleUrls: ['./device-search.component.scss'],
})
export class DeviceSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('deviceSearchGrid')
  public deviceSearchGrid!: CdwSsrmGridComponent<DeviceAsset>;
  @ViewChild(CdwBulkUpdateActionComponent)
  public bulkUpdateActionComponent?: CdwBulkUpdateActionComponent;

  @Output() updateDrawerContent = new EventEmitter<DeviceAsset>();
  @Input() filtersChange = new EventEmitter<SelectedFilterField[]>();
  private readonly destroy$ = new Subject<void>();
  private readonly deviceFilterBarId = 'deviceSearch';
  // localStorage key + recovery dedup set for the force-refresh flow.
  // Persists correlationIds across browser restarts so an in-flight Device Get
  // Job can be resumed and its result applied via `refreshGrid()`.
  private static readonly FORCE_REFRESH_STORAGE_KEY = 'cdw:forceRefreshJobs';
  private recoveredCorrelationIds = new Set<string>();

  // Modified-cell background — `colors.primary.gray.100` (#D9DFEA) per
  // UX direction. Resolved through the existing CSS-var pipeline so any
  // future palette change in tailwind.config.js + colors.scss flows
  // through automatically.
  public readonly deviceModifiedCellColor = TailwindColors.gray100;
  public readonly gray50 = TailwindColors.gray50;
  public readonly gray300 = TailwindColors.gray300;
  public readonly gray400 = TailwindColors.gray400;
  public readonly primaryWhite = TailwindColors.primaryWhite;

  // EDUS-2163 - host-supplied deprovision reason vocabulary; passed
  // into the bulk-update drawer so the lib doesn't import device-specific copy.
  public readonly deprovisionReasons = COMMON_DEPROVISION_REASONS;

  public selectedFilters$: Observable<SelectedFilterField[]>;

  // Date/time settings observable
  dateTimeSettings$!: Observable<DateTimeSettings>;
  private currentDateTimeSettings!: DateTimeSettings;

  public filterBarSettings: FilterBarField[] = [];

  public selectedDesignation: AccessManagementDesignationRecord | null = null;
  public isLoading = false;
  public isRefreshing = false;
  // gridApi/dataSource fields removed (Brian's #3 — never assigned, never read
  // after SSRM migration; the SSRM grid manages its own gridApi internally).
  public selectedFilterBarFields!: string[];
  public itemLabel = 'device';
  public deviceSearchComponentId = 'DeviceSearchComponent';
  public filterBySelectedColumnName = 'id';
  /**
   * Fields the SSRM grid must include in every block fetch's `selectFields`
   * regardless of column visibility. `id` and `isStale` are needed by the
   * grid itself (`getRowId`, PreviewIcon); the nested paths are needed
   * for bulk-action audit-log enrichment.
   *
   * Note: `status` lives at `vendorProperties.google.status` — there is
   * no top-level `status` field on the DeviceAsset entity. Backend
   * `selectFields` validation lives in `saas-device-api`'s devices.service.
   */
  public readonly alwaysRequiredFields: string[] = [
    'id',
    'isStale',
    'serialNumber',
    'vendorId',
    // EDUS-2618: `make` is NOT a filterable/searchable field in the UI (there is no
    // Make filter), but the Model column's valueGetter concatenates make + model to
    // render "HP Chromebook 14 G7". computeSelectFields() projects only the visible
    // columns plus this list, so dropping `make` here would blank the brand.
    'make',
    'vendorProperties.google.status',
    'vendorProperties.google.orgUnitPath',
  ];
  public readonly searchFieldMask: string[] = [
    'vendorProperties.google.orgUnitPath',
    'annotatedAssetId',
    'annotatedUser',
    'annotatedLocation',
    'notes',
    'serialNumber',
    // EDUS-2618: no 'make' here — the single Model filter searches both DB columns.
    'model',
    'status',
    'id',
    'lastSync',
    'vendorProperties.google.firstEnrollmentTime',
    'vendorProperties.google.lastEnrollmentTime',
    'vendorProperties.google.autoUpdateThrough',
    'batteryHealth',
    'vendorProperties.google.diskUsedCategory',
    'osVersionStatus',
    'osVersion',
    'vendorId',
    'vendorProperties.google.lastKnownUser',
  ];

  // Track filter state for URL updates
  public currentFilters: SelectedFilterField[] = [];
  public initialPage?: number;

  // === cross-page selection wiring ===
  /**
   * Derived observable bridging the state service's `selectedIds$`
   * (`Observable<(string|number)[]>`) to the SSRM grid's expected shape
   * (`Observable<Set<string>>`). Done in the consumer rather than
   * changing the state service's contract — other consumers (legacy
   * grid in audit-log-job-detail, etc.) still use the array form.
   */
  public selectedIdSet$: Observable<Set<string>> =
    this.deviceGridStateService.selectedIds$.pipe(
      map((ids) => new Set(ids.map(String)))
    );

  /**
   * bridge from `CdwSearchGridStateService.updatingIds$`
   * (`Observable<Set<string|number>>`) to the SSRM grid's expected
   * `[updatingIds$]` shape (`Observable<Set<string>>`). Mirrors the
   * the `selectedIdSet$` pattern — keeps the state service contract
   * unchanged for the 9 other (legacy clientSide) grid consumers.
   */
  public updatingIdsAsStringSet$: Observable<Set<string>> =
    this.deviceGridStateService.updatingIds$.pipe(
      map((ids) => new Set(Array.from(ids).map(String)))
    );

  /**
   * @deprecated Phase 9 of EDUS-2485 deleted the materialize-IDs path
   * that backed `[fetchAllIds]` on the grid. The Wipe Profiles auto-
   * trigger workflow (executeWipeProfilesWorkflow at the bottom of this
   * file) still calls this arrow — kept as a stub returning `of([])` so
   * the workflow's auto-trigger no-ops gracefully until a follow-up
   * rewires it to use the server-side `selectByFilter` callback (or the
   * Phase 10 bulk-action server-side resolution path).
   */
  public fetchAllDeviceIds = (): Observable<string[]> => of([]);

  // === Server-side selection store callbacks (Phase 9 of EDUS-2485) ===
  // These 5 arrow methods are wired to the grid library's new callback
  // @Inputs ([selectByFilter], [deselectByFilter], [selectByIds],
  // [deselectByIds], [clearAllSelected]). Each forwards to the
  // DevicesDataService method added in Phase 8, supplying the per-grid
  // gridSessionId minted in CdwSsrmGridComponent.ngOnInit.
  //
  // The grid component owns the sessionId and passes it through to its
  // datasource on every call; we read it back via `deviceSearchGrid` here
  // (which exposes the public `gridSessionId` getter as part of its public
  // API). For the rare null case (component not yet view-init'd, etc.)
  // we fall back to an empty string — the data-service helper omits the
  // header when sessionId is empty, which the backend treats as
  // "non-selection-aware call".

  public selectByFilterCallback = (
    filter: SelectedFilterField[]
  ): Observable<{ count: number }> =>
    this.devicesDataService.selectByFilter(
      filter as unknown as Parameters<typeof this.devicesDataService.selectByFilter>[0],
      this.currentGridSessionId()
    );

  public deselectByFilterCallback = (
    filter: SelectedFilterField[]
  ): Observable<{ count: number }> =>
    this.devicesDataService.deselectByFilter(
      filter as unknown as Parameters<typeof this.devicesDataService.deselectByFilter>[0],
      this.currentGridSessionId()
    );

  public selectByIdsCallback = (
    ids: string[]
  ): Observable<{ count: number }> =>
    this.devicesDataService.selectByIds(ids, this.currentGridSessionId());

  public deselectByIdsCallback = (
    ids: string[]
  ): Observable<{ count: number }> =>
    this.devicesDataService.deselectByIds(ids, this.currentGridSessionId());

  public clearAllSelectedCallback = (): Observable<{ count: number }> =>
    this.devicesDataService.clearSelection(this.currentGridSessionId());

  /**
   * 250ms-debounced batch sync for individual checkbox click events from
   * the grid's `(selectionToggled)` Output. The grid emits one event per
   * row toggled (header-checkbox batch select fires N events at once).
   * We collect them in a Subject, debounce, group by op, and flush as
   * batched selectByIds/deselectByIds server calls — keeping the UX
   * responsive (optimistic node.data.isSelected update happens grid-side)
   * while collapsing wire chatter.
   */
  private readonly selectionToggleEvents$ = new Subject<{
    rowId: string;
    op: 'select' | 'deselect';
  }>();

  /**
   * Read the current grid session UUID from sessionStorage directly.
   *
   * Earlier draft used `this.deviceSearchGrid?.gridSessionId` but the
   * @ViewChild reference isn't assigned until afterViewInit, which runs
   * AFTER the embedded filter-bar's ngOnInit. The filter bar emits its
   * initial filter state during its own ngOnInit → onFilterBarChange
   * fires → clearAllSelected callback runs → this method returned ''
   * → backend got no x-cdw-grid-session header → HTTP 400. Reading from
   * sessionStorage directly bypasses the view-child timing window: the
   * grid's ngOnInit mints the UUID into sessionStorage synchronously
   * before any filter-bar emit, so it's always there when callbacks fire.
   */
  protected currentGridSessionId(): string {
    try {
      return sessionStorage.getItem('grid-session:device-search') ?? '';
    } catch {
      return '';
    }
  }

  public fieldMask$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(
    this.searchFieldMask
  );

  // Editable fields configuration
  private readonly editableFields: string[] = [
    'vendorProperties.google.orgUnitPath',
    'vendorProperties.google.annotatedUser',
    'vendorProperties.google.annotatedLocation',
    'vendorProperties.google.annotatedAssetId',
    'vendorProperties.google.notes',
  ];

  //  cell-changes Map (rowId → field → newValue) lives in the consumer
  // per the consumer-owned-edits architecture decision (Q2). The SSRM grid library tracks
  // touched cells internally for the modified-cell visual but doesn't own the
  // canonical edit collection — only the consumer knows the field shapes its
  // backend payload requires (id + deviceId + ...changes).
  private cellChanges = new Map<string, Map<string, unknown>>();
  /**
   *  count of cells with pending edits, derived from `cellChanges`
   * by summing the inner Map sizes. Bound to the SSRM grid's
   * `[editChangeCount]` so the toolbar Save button shows "Save Changes (N)"
   * and disables when zero. Updated synchronously in `onCellValueChanged`,
   * `onSaveChanges`, `onDiscardChanges`, and `clearCellChanges`.
   */
  public editChangeCount = 0;
  /**
   *  true while a cell editor is currently open.
   * Combined with `editChangeCount > 0` to drive the Cancel/Save bar
   * visibility — so the bar appears the moment a user clicks into an
   * editable cell, not only after they commit a value change. Without this,
   * the user could click → type → escape and never see Cancel/Save at all.
   */
  public isEditingActive = false;
  /**
   * True when the in-flight cell editor's value differs from the original
   * (snapshot at editing-started). Driven by the SSRM grid's
   * `(cellEditingDirtyChanged)` output — emits BEFORE the user commits
   * the cell (vs. `cellValueChanged` which fires post-commit), so the
   * Save button can enable while the user is still typing. Always
   * resets to false on cellEditingStopped, regardless of commit/cancel.
   */
  public isEditingDirty = false;
  /**
   *  true between Save click and job-started
   * detection. Drives the Save button's `[disabled]` and label
   * (`Saving...`). Cleared on the FIRST non-pending status from the
   * jobs Firestore stream — at that moment the grid exits edit mode and
   * per-cell saving spinners take over until the job finishes.
   *
   * Failure path (dispatch returns null): isSaving cleared synchronously
   * so the user can retry without losing pending edits.
   */
  public isSaving = false;
  /**
   * Combined predicate for whether the Cancel/Save edit bar should be
   * visible AND the embedded filter bar should be hidden. Encapsulated
   * here so the template binds in one place and the predicate stays
   * authoritative across both decisions.
   */
  public get editBarVisible(): boolean {
    return this.isEditingActive || this.editChangeCount > 0 || this.isSaving;
  }
  /**
   *  cells currently being saved by an in-flight
   * bulk-edit job. Map keyed by row id → set of field paths. Bound to the
   * SSRM grid's `[savingCells$]` to drive the per-cell `.saving-cell`
   * visual (indeterminate spinner pseudo-element). Populated at
   * job-started detection (snapshot of cellChanges at Save time);
   * cleared at job completion.
   *
   * Distinct from row-level `updatingIds$` (which signals
   * row-level greying via PreviewIcon) — savingCells is per-cell and
   * scoped to the in-cell-edit save flow specifically.
   */
  public readonly savingCells$ = new BehaviorSubject<Map<string, Set<string>>>(
    new Map()
  );
  /**
   * snapshot of `cellChanges` pushed to the SSRM grid library so
   * the factory can overlay user-edited values onto freshly-fetched rows
   * (preserving edits across SSRM block evict + refetch). Emits a NEW
   * Map snapshot on every cellChanges mutation so the library's
   * subscription sees a real change.
   */
  public readonly cellChanges$ = new BehaviorSubject<
    Map<string, Map<string, unknown>>
  >(new Map());
  /**
   * drives the SSRM grid's `[idAllowList$]` input. When the user
   * toggles "Show changes only", emits `Array.from(cellChanges.keys())`;
   * when toggled off (or auto-cleared on Cancel / Save success), emits
   * `undefined`. Each emit triggers `refreshServerSide({purge: true})`
   * inside the grid library.
   */
  public readonly idAllowList$ = new BehaviorSubject<string[] | undefined>(
    undefined
  );
  /**
   * "Show changes only" toggle state. When true, the grid is
   * filtered (server-side via `idAllowList$`) to only the rows with
   * pending edits. Auto-clears on Cancel-confirm and on Save-success;
   * stays on through Save-failure so the user keeps the filtered view to
   * retry.
   */
  public showChangesOnly = false;
  /**
   * "Show Selected Only" toggle state. Driven by the SSRM grid's
   * footer ((showSelectedOnlyChange) emit). When true, the grid is
   * filtered (server-side via `idAllowList$`) to only the currently-
   * selected rows. Mutually exclusive with `showChangesOnly` because
   * both toggles write to the same `idAllowList$` source-of-truth —
   * activating one deactivates the other (last-toggle-wins).
   *
   * Auto-clears when the selection drops to 0 (the footer also self-
   * hides at that point; without auto-clear the grid would stay frozen
   * on an orphaned ID list).
   */
  public showSelectedOnly = false;

  // Feature flag to display edit icons in column selector
  private readonly displayColumnEditIcons = true;

  public columnDefs: CdwColDef[] = [];

  // OU tree data for cell editor
  public ouTreeData: TreeNode[] = [];

  // Bulk edit drawer configuration
  public selectedDeviceIds: string[] = [];
  public selectedDeviceData: DeviceAsset[] = [];
  public bulkUpdateFieldConfigs: FieldUpdateConfig[] = [];
  public bulkEditActionConfig?: {
    visibleFields?: string[];
    defaultValues?: Array<{
      fieldName: string;
      updateType: string;
      value: unknown;
    }>;
    actionType?: string;
  };

  /**
   * EDUS-2626: reminder text passed to the bulk-action drawer's
   * `[crossPageReminderText]` input. Returns the copy only for the four
   * actions in `CROSS_PAGE_REMINDER_ACTIONS`; undefined hides the band.
   */
  get bulkEditCrossPageReminder(): string | undefined {
    const t = this.bulkEditActionConfig?.actionType as DeviceAction | undefined;
    return t && CROSS_PAGE_REMINDER_ACTIONS.has(t) ? CROSS_PAGE_REMINDER_TEXT : undefined;
  }

  /**
   * Wipe Profiles workflow auto-trigger state.
   *
   * `currentWorkflowType` is read from router navigation state at
   * construction (e.g. when the dashboard's "Wipe Profiles" card
   * navigates here with `state: { workflowType: 'profile-wipe' }`).
   * Survives only the initial navigation — page refresh wipes it so
   * the workflow doesn't re-trigger on F5.
   *
   * `wipeProfilesFilterActive` flips true when the filter chip set
   * matches the workflow's expected pattern (diskUsedCategory ∈
   * {Eighty, Ninety}). `wipeProfilesWorkflowTriggered` is the one-shot
   * guard that prevents re-trigger on subsequent paginations or
   * dataLoaded fires within the same session.
   */
  private currentWorkflowType: string | undefined;
  private wipeProfilesFilterActive = false;
  private wipeProfilesWorkflowTriggered = false;

  // Bulk update action inputs
  public customerId = '';
  public userEmail = '';
  // Display name used in export attribution (Skipped Devices export per
  // PR #880 EDUS-2163 + future export sidecars). Selector returns '' until
  // the auth pipeline populates it.
  public userName = '';
  // Firebase UID — string per `selectFbUserId`'s contract.
  public fbUserId = '';
  private dbUserId = 0;
  private getBaseColumnDefs(): CdwColDef[] {
    return columnLookupToColDefs(
      DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER,
      DEVICE_SEARCH_COLUMN_LOOKUP,
      this.currentDateTimeSettings
    );
  }

  constructor(
    public devicesDataService: DevicesDataService,
    private router: Router,
    private store: Store,
    private dialog: MatDialog,
    public deviceGridStateService: CdwSearchGridStateService,
    private snackbarService: SnackbarService,
    private orgUnitsDataService: OrgUnitsDataService,
    private navbarButtonsRegistry: NavbarButtonsRegistryService,
    private jobMonitorService: JobMonitorService,
    private readonly filterStrategy: FilterBarParamStrategy,
    readonly _actions$: Actions,
    private readonly deviceExportImport: DeviceExportImportService,
    private gtmService: GoogleTagManagerService,
  ) {
    this.selectedFilters$ = this.store.select(
      selectSelectedFilters(this.deviceFilterBarId)
    );
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);
    this.filterBarSettings = DeviceSearchFilterSettings.filter(
      (filterBarField: FilterBarField): boolean =>
        this.searchFieldMask.includes(filterBarField.name)
    );

    // Wire validateFn for paste-mode fields so the Apply button can
    // surface unmatched values back to the user (EDUS-2671).
    this.filterBarSettings = this.filterBarSettings.map((field) => {
      if (!['serialNumber', 'annotatedAssetId'].includes(field.name)) {
        return field;
      }
      return {
        ...field,
        fieldMatchers: field.fieldMatchers.map((matcher) =>
          matcher.name !== 'multiTextSearch'
            ? matcher
            : {
                ...matcher,
                options: {
                  ...matcher.options,
                  validateFn: (values: string[]) =>
                    this.devicesDataService.validateFilterValues(field.name, values),
                },
              }
        ),
      };
    });

    // read workflow type from router navigation state. Must
    // happen in the constructor — by the time ngOnInit runs,
    // `getCurrentNavigation()` returns null. `state.workflowType` is set
    // by callers like the dashboard's "Wipe Profiles" card via
    // `router.navigate(['/deviceSearch'], { state: { workflowType: 'profile-wipe' } })`.
    const navigation = this.router.getCurrentNavigation();
    this.currentWorkflowType = navigation?.extras?.state?.workflowType as
      | string
      | undefined;

    // workflow-type detection (the wipe-profiles auto-trigger
    // path) was removed here. Re-lands when bulk actions and
    // workflow auto-triggers come back.
  }

  /**
   * Build column definitions dynamically based on editable fields configuration
   */
  private buildColumnDefs(): void {
    const defaultSortField =
      DEVICE_SEARCH_COLUMN_LOOKUP[DEVICE_SEARCH_SORT_TIEBREAKER.name]?.sqlName;

    // prepend the row-checkbox column. The legacy grid auto-injected
    // this when [showCheckboxSelection] was true; the SSRM grid is generic
    // (consumer chooses), so we add it explicitly here. Cross-page selection
    // round-trip is handled by the SSRM grid's [selectedIds$] /
    // (selectionChanged) wiring in the template (cross-page selection wiring).
    //
    // headerComponent: 'ssrmHeaderCheckbox' uses the SSRM grid's custom
    // header checkbox component (registered in CdwSsrmGridComponent's
    // gridOptions.components). AG-Grid 28's built-in
    // `headerCheckboxSelection: true` doesn't render under SSRM mode, so
    // the SSRM grid library provides this drop-in replacement.
    const checkboxCol: CdwColDef = {
      // EDUS-2517: explicit colId matches the convention from the legacy
      // `selectionColumn` constant (libs/.../config/cdw-selection-col.constant.ts:4)
      // so downstream code that filters out system columns by colId works.
      // Without this, AG-Grid auto-generates an opaque id and the export
      // dialog's column-count filter (cdw-export-dialog.component.ts:93,
      // `name !== 'selectCheckboxComponent'`) misses the checkbox column,
      // overcounting visible data columns by 1.
      colId: 'selectCheckboxComponent',
      checkboxSelection: true,
      headerComponent: 'ssrmHeaderCheckbox',
      width: 50,
      pinned: 'left',
      sortable: false,
      filter: false,
      // SSRM grid's `defaultColDef.resizable = true` would otherwise
      // render a resize handle at this column's right edge — visually
      // wrong because there's nothing meaningful to resize a system
      // checkbox column to.
      resizable: false,
      suppressMovable: true,
      // Locked to the far left: no column can be dragged into its slot and a
      // persisted column-layout restore cannot reorder it.
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
    };

    const baseColumns = this.getBaseColumnDefs().map((colDef) => {
      const isEditable = this.editableFields.includes(colDef.field || '');
      const isOrgUnitPath =
        colDef.field === 'vendorProperties.google.orgUnitPath';

      // `editable: true` is REQUIRED for the SSRM grid library's
      // `wrapColumnDefs` pass to install the editable + cellClass
      // callbacks. Without it, the library's wrapping skips this column
      // and AG-Grid treats it as read-only.
      const editableConfig =
        isEditable && this.displayColumnEditIcons
          ? {
              editable: true,
              headerClass: 'editable-column-header',
              columnSelectorIcon: 'edit',
              headerIcon: 'edit',
            }
          : {};

      // Add tree picker cell editor for orgUnitPath column
      const cellEditorConfig = isOrgUnitPath
        ? {
            cellEditor: TreePickerCellEditorComponent,
            cellEditorParams: {
              dataSource: () => this.ouTreeData || [],
              height: '300px',
              filterPlaceholder: 'Search OUs...',
              filterLabel: 'Filter',
            },
          }
        : {};

      const defaultSortConfig =
        colDef.field === defaultSortField
          ? { sort: DEVICE_SEARCH_SORT_TIEBREAKER.direction?.toLowerCase() as 'asc' | 'desc', sortIndex: 0 }
          : {};

      return {
        ...colDef,
        ...editableConfig,
        ...cellEditorConfig,
        ...defaultSortConfig,
      } as CdwColDef;
    });

    //  insert the View column after the checkbox so the
    // user has a dedicated eye-icon affordance for navigating to the
    // device summary. Row-click navigation was removed (the entire row
    // was a click target previously) — only the View icon now navigates.
    // See onCellClicked: filters to colId === 'previewIconComponent' so
    // clicks elsewhere in the row don't fire the route change.
    this.columnDefs = [checkboxCol, previewColumn, ...baseColumns];
  }

  // -------------------------------------------------------------------------
  // SSRM selection-lock failsafe — observables + predicate bound to the grid.
  // gridSessionUuid is read from sessionStorage via currentGridSessionId() and
  // does NOT change for the lifetime of this component; the selectors are
  // factory-instantiated once per init.
  // -------------------------------------------------------------------------
  /** True while a bulk job is in flight for THIS browser tab's grid session. */
  public lockActive$!: Observable<boolean>;
  /** First stuck job for this grid session (or null), drives the banner. */
  public stuckJob$!: Observable<JobExecutionStatus | null>;
  /**
   * Predicate consumed by `<lib-cdw-ssrm-grid [selectedRowSpinnerPredicate]>`
   * — flags every currently-selected row with `isUpdating: true` so the
   * existing PreviewIconComponent renders the spinner. Defined as a class
   * field (not a method) so the reference is stable across change detection;
   * AG-Grid otherwise re-runs the augmentation pipeline unnecessarily.
   */
  public lockedSelectedSpinnerPredicate = (row: {
    id: string | number;
    isSelected?: boolean;
  }): boolean => !!row.isSelected;
  /**
   * Synchronous mirror of lockActive$, mutated by the subscription set up
   * in ngOnInit. Used by `openBulkEditDrawer` to gate the bulk-action entry
   * point without an `async` rewrite. Reading the latest emitted value of
   * an Observable synchronously requires either toPromise/firstValueFrom
   * (async) or a mirror field — the mirror field is preferable here so
   * the gate stays a synchronous early return.
   */
  private currentLockActiveSnapshot = false;
  private isLockActiveSnapshot(): boolean {
    return this.currentLockActiveSnapshot;
  }

  ngOnInit(): void {
    // Navbar callbacks + wipe-profiles auto-trigger re-land .

    // Show loading indicator when component initializes
    this.isLoading = true;

    // -----------------------------------------------------------------------
    // SSRM selection-lock failsafe — placeholder observables.
    //
    // Real selector wiring happens in ngAfterViewInit because the grid mints
    // gridSessionUuid into sessionStorage during ITS ngOnInit, which runs
    // AFTER the parent's ngOnInit (Angular initializes children during the
    // parent's first CD pass, not before ngOnInit). Reading sessionStorage
    // here on a fresh tab returns empty, which would lock lockActive$ to a
    // constant `of(false)` and the lock UI would never engage.
    //
    // Pessimistic hydration ALSO runs in ngAfterViewInit (see below) — it
    // depends on the same gridSessionUuid being available.
    // -----------------------------------------------------------------------
    this.lockActive$ = of(false);
    this.stuckJob$ = of(null);

    // Subscribe to date/time settings changes — rebuild columnDefs so any
    // date/timestamp formatting picks up new locale settings. The legacy
    // grid also called gridApi.refreshCells({force:true}); SSRM evicts
    // and re-fetches blocks naturally on any in-cache mutation, so we
    // skip that here. (If we observe stale cell renders post-settings-
    // change in sandbox, can add a `purgeAndRefresh()` call.)
    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentDateTimeSettings = settings;
        this.buildColumnDefs();
      });

    // Check if we're returning from device summary page
    const savedPage = sessionStorage.getItem('deviceSearchReturnPage');
    if (savedPage) {
      this.initialPage = parseInt(savedPage, 10);
      sessionStorage.removeItem('deviceSearchReturnPage');
    }

    // Restore saved filters when returning via in-app navigation (no URL filter params)
    const deviceSearchPrefix =
      FILTER_QUERY_PARAM_PREFIXES[FilterBarId.DeviceSearch];
    const hasUrlFilters = deviceSearchPrefix
      ? new URLSearchParams(window.location.search).has(deviceSearchPrefix)
      : false;
    if (!hasUrlFilters) {
      try {
        const raw = sessionStorage.getItem(DEVICE_SEARCH_FILTER_STORAGE_KEY);
        if (raw) {
          const urlStateItems = JSON.parse(raw);
          const filters = this.filterStrategy.convertToInput(
            urlStateItems,
            this.filterBarSettings
          );
          if (filters.length > 0) {
            this.store.dispatch(
              FilterActions.updateFilters({
                filters,
                filterBarId: this.deviceFilterBarId,
              })
            );
          }
        }
      } catch {
        // Corrupted sessionStorage data — clear and continue
        sessionStorage.removeItem(DEVICE_SEARCH_FILTER_STORAGE_KEY);
      }

      // EDUS-2608: default to "Device Status: Active" on fresh entry so users
      // don't see disabled/deprovisioned units by default. Mirrors the
      // Dashboard's initialization (dashboard.component.ts:174-201). Gated on
      // `!hasUrlFilters` so shared deep-links (e.g., Dashboard chart drill-in)
      // are respected. take(1) reads the store snapshot after the
      // sessionStorage-restore dispatch above has landed; only injects when
      // no filters were restored.
      this.selectedFilters$.pipe(take(1)).subscribe((existingFilters) => {
        if (existingFilters && existingFilters.length > 0) {
          return;
        }
        const defaultFilters = this.filterStrategy.convertToInput(
          [
            {
              field: 'status',
              operator: 'equals' as const,
              value: GoogleDeviceStatus.ACTIVE,
            },
          ],
          this.filterBarSettings
        );
        this.store.dispatch(
          FilterActions.updateFilters({
            filters: defaultFilters,
            filterBarId: this.deviceFilterBarId,
          })
        );
      });
    }

    // Build column definitions immediately
    this.buildColumnDefs();

    // Track filter state from selectors. The SSRM grid embeds its own
    // filter bar (drop-in filter-bar contract), so we no longer need
    // to imperatively refresh on filter change — the grid's own
    // `onFilterBarChange` handler does `refreshServerSide({purge:true})`.
    // We keep `currentFilters` in sync for sessionStorage persistence and
    // for the `[initialSelectedFilterFields]` @Input.
    this.selectedFilters$
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((filters) => {
        this.currentFilters = filters;
        //  filter changes invalidate any in-flight edits — the rows
        // the user was editing may not match the new filter, leading to
        // ambiguous save scope. Clear local cellChanges + drop edit mode
        // (mirrors the banner-clear pattern, which clears selection
        // on filter change for the same scope-ambiguity reason).
        this.clearCellChanges();
        this.deviceSearchGrid?.exitEditMode();
        // detect Wipe Profiles workflow trigger conditions
        // on every filter change. Sets `wipeProfilesFilterActive` only;
        // the actual trigger fires from `onDataLoaded` once the grid
        // has rows.
        this.checkForWipeProfilesWorkflow(filters);
      });

    // Refresh grid when cache refresh completes (full-asset-sync-workflow
    // finishes). Use the SSRM grid's public `purgeAndRefresh()`
    // — equivalent to the legacy grid's `refreshGrid()`.
    this.store
      .select(selectIsFullAssetSyncRunning)
      .pipe(distinctUntilChanged(), pairwise(), takeUntil(this.destroy$))
      .subscribe(([wasRefreshing, isRefreshing]) => {
        if (wasRefreshing && !isRefreshing && this.deviceSearchGrid) {
          this.deviceSearchGrid.purgeAndRefresh();
        }
      });

    // re-enable force-refresh-by-IDs (`refreshRows$`) and
    // post-bulk-action cache-update (`updateRows$`) subscriptions.
    // Visible-row spinner state is driven by the SSRM grid's
    // `[updatingIds$]` augmentation layer; fed from the state
    // service's new `updatingIds$` observable; we just call
    // `markAsUpdating` / `clearUpdating` here.
    this.deviceGridStateService.refreshRows$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => {
        const stringIds = ids.map(String);
        const correlationId = crypto.randomUUID();
        const customerId = parseInt(this.customerId, 10);

        this.markRowsAsUpdating(stringIds);
        this.saveForceRefreshToLocalStorage(correlationId, customerId);

        this.devicesDataService
          .forceRefreshByIds(stringIds, correlationId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((response) => {
            // True HTTP failure — surface the underlying error and clear
            // the spinners so the user can retry. Distinguishes from the
            // legitimate "no job needed, cache fresh" branch below.
            if (response.error) {
              this.removeForceRefreshFromLocalStorage(correlationId);
              this.clearRowsUpdating(stringIds);
              this.snackbarService.showError(
                `Failed to refresh devices: ${response.error}`,
                'Close',
                { horizontalPosition: 'center', duration: 5000 }
              );
              return;
            }

            // Cache already fresh (no job needed) — pull latest and
            // confirm to the user. clearRowsUpdating happens inside
            // fetchAndUpdateRows.
            if (!response.jobId) {
              this.removeForceRefreshFromLocalStorage(correlationId);
              this.fetchAndUpdateRows(stringIds);
              this.snackbarService.showMessage(
                'Devices refreshed from cache',
                'Close',
                { horizontalPosition: 'center', duration: 3000 }
              );
              return;
            }

            // Job dispatched — monitor via Firestore correlationId watch.
            this.jobMonitorService
              .monitorByCorrelationIdWithCallbacks(
                customerId,
                correlationId,
                () => {
                  this.removeForceRefreshFromLocalStorage(correlationId);
                  this.fetchAndUpdateRows(stringIds);
                  this.snackbarService.showMessage(
                    'Device refresh completed successfully',
                    'Close',
                    { horizontalPosition: 'center', duration: 3000 }
                  );
                },
                () => {
                  this.removeForceRefreshFromLocalStorage(correlationId);
                  this.clearRowsUpdating(stringIds);
                  this.snackbarService.showError(
                    'Device refresh job failed',
                    'Close',
                    { horizontalPosition: 'center', duration: 5000 }
                  );
                }
              )
              .pipe(takeUntil(this.destroy$))
              .subscribe();
          });
      });

    // Cache-already-fresh path (e.g. post-bulk-edit): no force-refresh job,
    // just re-fetch the row data and apply via SSRM transaction.
    this.deviceGridStateService.updateRows$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => {
        const stringIds = ids.map(String);
        this.fetchAndUpdateRows(stringIds);
      });

    // register navbar callbacks. The navbar registry exposes
    // these as nullable slots; assigning here lets the navbar action
    // dropdowns (Wipe Profiles, Factory Reset, Move OU, Edit Devices,
    // etc.) reach back into this component to open the bulk-edit drawer
    // and trigger audit-log creation. Cleared in ngOnDestroy so a
    // subsequent navigation to a non-DeviceSearch page doesn't keep
    // stale closures alive.
    this.navbarButtonsRegistry.openBulkEditDrawer = (actionConfig) =>
      this.openBulkEditDrawer(actionConfig);

    // Bridge for navbar-driven SSRM bulk dispatches (Refresh Selected,
    // Move-to-last-user-OU) that need a full grid refresh on job
    // completion. Without an id list the registry's
    // `monitorBulkActionJob` would no-op `updateRows([])`, leaving
    // stale row data on screen. The grid @ViewChild lives here, not
    // on the global navbar registry, hence the callback bridge.
    this.navbarButtonsRegistry.requestFullGridRefresh = () =>
      this.deviceSearchGrid?.purgeAndRefresh();

    // Wire export/import dialogs through DeviceExportImportService.
    // The service holds no per-call state — we pass grid + filters +
    // selection + identity each invocation. selectedIds is read at
    // dispatch time so each click reflects the current selection.
    // EDUS-2486 Hit-list #7: SSRM consumers don't have a client-side
    // selection id list — selection lives in Redis on the server. Pass
    // `selectedCount` (state-service mirrors the server-authoritative
    // count via the page-fetch enrichment) plus `gridSessionId` +
    // `viewKey` so the export dialogs can dispatch with
    // `useSelection: true` and the saas-device-api materializes from
    // Redis at dispatch time. `selectedIds: []` is preserved for
    // interface compatibility — DeviceExportImportService prefers
    // `selectedCount` when provided.
    const ssrmSelectionParams = () => ({
      selectedIds: [] as string[],
      selectedCount: this.deviceGridStateService.getSelectedCount(),
      gridSessionId:
        sessionStorage.getItem('grid-session:device-search') ?? undefined,
      viewKey: 'device-search',
    });
    this.navbarButtonsRegistry.openExportDialog = () =>
      this.deviceExportImport.openExportDialog({
        grid: this.deviceSearchGrid,
        filters: this.currentFilters,
        ...ssrmSelectionParams(),
        identity: this.getIdentity(),
      });
    this.navbarButtonsRegistry.openExportForBulkEditDialog = () =>
      this.deviceExportImport.openExportForBulkEditDialog({
        grid: this.deviceSearchGrid,
        filters: this.currentFilters,
        ...ssrmSelectionParams(),
        identity: this.getIdentity(),
      });
    this.navbarButtonsRegistry.openImportDialog = () =>
      this.deviceExportImport.openImportDialog({
        grid: this.deviceSearchGrid,
        filters: this.currentFilters,
        ...ssrmSelectionParams(),
        identity: this.getIdentity(),
        // EDUS-2454: refresh the grid as soon as the import job hits
        // `finished`. Service fires this from inside its own pipe so
        // the grid is fresh by the time the user dismisses the dialog.
        onImportComplete: () => this.deviceSearchGrid?.purgeAndRefresh(),
      });
    this.navbarButtonsRegistry.onDeviceActionJobCreated = (params) =>
      this.handleActionsExecuted({
        jobId: params.jobId,
        updates: params.deviceIds.map((deviceId) => ({ deviceId })),
        command: params.command,
      });
    // in-cell-save concurrency gate (per locked Q2). The
    // navbar consults this on every disabled-state evaluation to grey
    // out bulk-action buttons during the brief save dispatch window.
    this.navbarButtonsRegistry.isInCellSaveInFlight = () => this.isSaving;

    // Phase 9 of EDUS-2485: selectedIds$ mirror subscription removed.
    // Per-row selection state is now server-authoritative (node.data.isSelected
    // populated by Phase 3's enrichment); the consumer doesn't track
    // selectedDeviceIds locally. The bulk-edit drawer's [selectedItems]
    // binding still reads selectedDeviceIds (kept as empty array — Phase 10
    // rewires the drawer to consume the server-side selection store via
    // useSelectionAsFilter pagination). The footer auto-clear of
    // showSelectedOnly when selection drops to 0 moves to the
    // (selectionCount) Output handler from Phase 6.

    // === Phase 9: debounced batch sync of (selectionToggled) events ===
    // The grid emits one selectionToggled per row toggled (header-checkbox
    // batch fires N events at once). Debounce 250ms then group by op and
    // flush as batched selectByIds/deselectByIds server calls. Errors
    // surface via snackbar; the next page-fetch reconciles UI from the
    // server's truth (graceful degrade if Redis is down).
    this.selectionToggleEvents$
      .pipe(
        bufferTime(250),
        filter((events) => events.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((events) => {
        const selects: string[] = [];
        const deselects: string[] = [];
        for (const e of events) {
          (e.op === 'select' ? selects : deselects).push(e.rowId);
        }
        const sessionId = this.currentGridSessionId();
        // On success: push the returned server count to the state service so
        // the footer + banner update immediately. Without this, the count
        // would only update on the next page-fetch (via the (selectionCount)
        // Output) — and click-batches don't trigger a page-fetch, so the
        // footer would stay at 0 forever and the banner would never appear.
        // Race note: if both selects+deselects fire in parallel, last
        // response wins. Brief stale-count is fine — the next page-fetch
        // re-attaches the canonical value via enrichment.
        if (selects.length > 0) {
          this.devicesDataService
            .selectByIds(selects, sessionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.deviceGridStateService.setSelectedCount(res.count);
                // EDUS-2521: see deselects branch comment below.
                if (this.showSelectedOnly) {
                  this.deviceSearchGrid?.purgeAndRefresh();
                }
              },
              error: (err: unknown) => this.handleSelectionWriteError(err),
            });
        }
        if (deselects.length > 0) {
          this.devicesDataService
            .deselectByIds(deselects, sessionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.deviceGridStateService.setSelectedCount(res.count);
                // EDUS-2521: in "Show Selected Only" mode, deselected rows
                // must disappear from the view (the filter's contract is
                // "only currently-selected rows"). The server-side filter
                // (useSelectionAsFilter → Redis selection-set membership)
                // already excludes them on a fresh fetch, but the SSRM
                // grid's cached blocks still hold them until a refresh.
                // Trigger one now that the server has confirmed the
                // mutation — the 250ms bufferTime above already coalesces
                // burst clicks, so this fires at most once per batch.
                // Inverse direction (selects branch above) gets the same
                // treatment for defensive parity, e.g., a toggle-then-
                // re-toggle within the debounce window.
                if (this.showSelectedOnly) {
                  this.deviceSearchGrid?.purgeAndRefresh();
                }
              },
              error: (err: unknown) => this.handleSelectionWriteError(err),
            });
        }
      });

    // user identity for bulk-action audit log creation. Both
    // selectors return '' until the auth pipeline populates them, so the
    // initial subscribe + every later refresh just overwrite the field.
    // No distinctUntilChanged needed — assignment is cheap.
    this.store
      .select(selectUserEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        this.userEmail = email;
      });
    this.store
      .select(selectFbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((uid) => {
        this.fbUserId = uid;
      });
    this.store
      .select(selectDbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.dbUserId = id ?? 0;
      });
    this.store
      .select(selectUserName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        this.userName = name;
      });

    // load OU tree for the bulk-edit drawer's OU-picker dropdown.
    // The cell editor for orgUnitPath also reads from `ouTreeData` (existing
    // since the original migration), so this single load serves both consumers. After the
    // load completes, build the bulk-edit field configs which depend on the
    // OU tree being populated. Error path falls back to an empty tree so
    // the drawer still opens with text-only fields.
    this.orgUnitsDataService
      .getOrgUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ous) => {
          this.ouTreeData = ous;
          this.initializeBulkEditConfigs();
        },
        error: () => {
          this.ouTreeData = [];
          this.initializeBulkEditConfigs();
        },
      });

    // Subscribe to customerId so a browser-restart recovery can resume any
    // in-flight force-refresh jobs persisted to localStorage.
    this.store
      .select(selectCustomerId)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((customerId) => {
        const prevCustomerId = this.customerId;
        this.customerId = customerId;
        //  customer change invalidates in-flight edits — the
        // editing user has switched tenants, any pending edits would target
        // the wrong customer's devices. Skip on the initial empty→firstId
        // transition (no edits possible yet at app boot).
        if (prevCustomerId && prevCustomerId !== customerId) {
          this.clearCellChanges();
          this.deviceSearchGrid?.exitEditMode();
        }
        if (customerId) {
          this.recoverPendingForceRefreshJobs(parseInt(customerId, 10));
        }
      });

    // navbar "Refresh Cache" + "Actions" buttons read selection
    // synchronously at button-registration time and require an explicit
    // re-registration call when selection changes. This subscription
    // bridges the gap. Needed because the row-spinner's
    // force-refresh flow can't be sandbox-validated without it ("Refresh
    // Selected" stays disabled indefinitely otherwise).
    //
    // Why selectedCount$ over selectedIds$: only count matters for the
    // disabled-state evaluation (`selectedIds.length > 0`), and a number
    // is cheaper to distinct-check than an array. Toggling identical-count
    // selections doesn't change disabled state, so suppressing those
    // emissions is desirable.
    this.deviceGridStateService.selectedCount$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.navbarButtonsRegistry.updateDeviceActionsDisabledState();
      });
  }

  /**
   * Real wiring of the SSRM selection-lock observables. Runs AFTER the grid
   * component's ngOnInit, by which time the grid has minted gridSessionUuid
   * into sessionStorage. Replaces the placeholder of(false)/of(null) set in
   * the parent's ngOnInit so the @Input bindings on `<lib-cdw-ssrm-grid>`
   * and `<app-stuck-job-banner>` re-evaluate against real selectors via
   * ngOnChanges on the next CD pass.
   *
   * Pessimistic hydration also lives here — a same-tab refresh that occurred
   * mid-job will have both `grid-session:device-search` AND the ssrm-lock
   * entry (key shape owned by `buildSsrmLockStorageKey`) in sessionStorage;
   * we restore the lock state synchronously here so checkboxes stay
   * disabled across the refresh.
   *
   * For first-visit (fresh tab) the grid has just minted a brand-new UUID,
   * there's no prior lock entry, so hydration is a no-op — the selectors are
   * still wired and will pick up `bulkJobEnqueued` actions when the user
   * triggers them later.
   */
  ngAfterViewInit(): void {
    const gridSessionUuid = this.currentGridSessionId();
    if (!gridSessionUuid) {
      // sessionStorage genuinely unavailable (test harness, sandboxed iframe).
      // Lock UX gracefully degrades — falsy lockActive$ from ngOnInit stands.
      return;
    }

    this.lockActive$ = this.store.select(
      selectIsLockActiveForGridSession(gridSessionUuid)
    );
    this.lockActive$
      .pipe(takeUntil(this.destroy$))
      .subscribe((active) => {
        this.currentLockActiveSnapshot = active;
      });
    this.stuckJob$ = this.store
      .select(selectStuckJobsForGridSession(gridSessionUuid))
      .pipe(map((jobs) => jobs[0] ?? null));

    // Pessimistic hydration from sessionStorage — restores lock state on
    // same-tab refresh so checkboxes are disabled BEFORE the Firestore
    // listener confirms.
    try {
      const raw = sessionStorage.getItem(
        buildSsrmLockStorageKey(gridSessionUuid)
      );
      if (raw) {
        const parsed = JSON.parse(raw) as {
          jobId?: number;
          selectedCount?: number;
          lockSetAt?: number;
        };
        if (
          typeof parsed.jobId === 'number' &&
          typeof parsed.selectedCount === 'number' &&
          typeof parsed.lockSetAt === 'number'
        ) {
          this.store.dispatch(
            lockHydratedFromStorage({
              gridSessionUuid,
              jobId: parsed.jobId,
              selectedCount: parsed.selectedCount,
              lockSetAt: parsed.lockSetAt,
            })
          );
        }
      }
    } catch {
      // sessionStorage unavailable or malformed entry — degrade silently.
      // Lock will activate from Firestore listener once it binds.
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // clear updating state (visible spinners) on teardown to avoid
    // stale entries surviving navigation.
    this.deviceGridStateService.clearAllUpdating();
    // null out navbar callbacks so a subsequent navigation
    // to a non-DeviceSearch page (or a destroyed DeviceSearch instance
    // being recreated) doesn't keep stale closures alive that would
    // call into a destroyed component instance.
    this.navbarButtonsRegistry.openBulkEditDrawer = null;
    this.navbarButtonsRegistry.requestFullGridRefresh = null;
    this.navbarButtonsRegistry.openExportDialog = null;
    this.navbarButtonsRegistry.openExportForBulkEditDialog = null;
    this.navbarButtonsRegistry.openImportDialog = null;
    this.navbarButtonsRegistry.onDeviceActionJobCreated = null;
    this.navbarButtonsRegistry.isInCellSaveInFlight = null;
    // drop any in-flight cell edits + reset edit-mode state on the
    // shared service.
    this.clearCellChanges();
    this.deviceGridStateService.setEditMode(false);
    // Complete the in-cell-edit subjects so any pending subscriptions
    // in the SSRM grid library get torn down with the consumer's
    // lifetime. (The library's takeUntil(destroy$) will also clean up,
    // but this is belt-and-suspenders.)
    this.savingCells$.next(new Map());
    this.savingCells$.complete();
    this.cellChanges$.next(new Map());
    this.cellChanges$.complete();
    this.idAllowList$.next(undefined);
    this.idAllowList$.complete();

    // Save current filters to sessionStorage for restoration on in-app navigation return
    if (this.currentFilters.length > 0) {
      const serializable = this.filterStrategy.convertFromInput(
        this.currentFilters
      );
      sessionStorage.setItem(
        DEVICE_SEARCH_FILTER_STORAGE_KEY,
        JSON.stringify(serializable)
      );
    } else {
      sessionStorage.removeItem(DEVICE_SEARCH_FILTER_STORAGE_KEY);
    }
    // Clear device search filters from store to prevent stale state
    // when navigating back from another page (e.g., dashboard chart click)
    this.store.dispatch(
      FilterActions.clearFilters({ filterBarId: this.deviceFilterBarId })
    );
  }

  onDeviceFiltersChanged(event: { filters: SelectedFilterField[] }): void {
    this.store.dispatch(
      FilterActions.updateFilters({
        filters: event.filters,
        filterBarId: this.deviceFilterBarId,
      })
    );

    // Track which filters users apply for product analytics
    if (event.filters.length > 0) {
      this.gtmService.pushUserAction({
        category: 'filtering',
        action: 'filters_applied',
        metadata: {
          filters_applied: event.filters.map((f) => f.field.name),
        },
      });
    }
  }

  /**
   * Public hook for external triggers (route guards, parent components) to
   * force a grid refresh. Maps to the SSRM grid's `purgeAndRefresh()`
   * — equivalent to the legacy grid's `refreshGrid()`.
   */
  refreshSearchGrid(): void {
    this.deviceSearchGrid?.purgeAndRefresh();
  }

  /**
   *  navigation moved off the row click and onto the View
   * column's eye icon. AG-Grid's `cellClicked` fires for every cell click
   * with column identity intact (unlike `rowClicked`), so we filter to the
   * preview column's colId before navigating. Other cell clicks (including
   * editable cells, where AG-Grid will start inline editing on the same
   * click thanks to `singleClickEdit: true`) are ignored here.
   */
  /**
   * Bulk-action drawer's `(bulkActionExecuted)` handler.
   *
   * Activates the SSRM selection lock, marks affected rows as updating,
   * and triggers the post-job refresh chain. Audit logging is handled by
   * the backend create-audit-log workflow step (EDUS-2491).
   */
  /**
   * Handle the user clicking "Cancel and unlock" on the stuck-job banner.
   * Dispatches `cancelJobRequested` which the cancelJob$ effect translates
   * into an optimistic local release (selectionLockReleaseRequested emitted
   * FIRST, spinners + lock clear instantly) plus the HTTP cancel call. The
   * API succeeds → snackbar; fails → snackbar with "may still be running"
   * caveat. Either way the lock stays released because the user explicitly
   * chose to escape.
   */
  public onCancelStuckJob(jobId: number): void {
    const customerIdNum = parseInt(this.customerId, 10);
    if (!Number.isFinite(customerIdNum)) {
      // No customer context — can only release locally; the cancelJob$
      // effect needs a real customerId to hit the API.
      return;
    }
    this.store.dispatch(
      cancelJobRequested({
        jobId,
        customerId: customerIdNum,
        gridSessionUuid: this.currentGridSessionId() || undefined,
      })
    );
  }

  public handleActionsExecuted(event: {
    jobId: number;
    updates: Record<string, unknown>[];
    fieldUpdates?: Array<{
      field: string;
      updateType: string;
      value: unknown;
    }>;
    command?: string;
    source?: string;
    analyticsMetadata?: LibraryBulkActionAnalyticsMetadata;
  }): void {
    const { jobId, updates, command } = event;

    // Track bulk action in GTM/GA4
    if (event.analyticsMetadata) {
      const validActionTypes: Set<string> = new Set([
        'move-ou', 'disable', 'enable', 'deprovision',
        'profile-wipe', 'factory-reset', 'edit-import'
      ]);
      const actionType = validActionTypes.has(event.analyticsMetadata.action_type)
        ? event.analyticsMetadata.action_type
        : 'unknown';

      this.gtmService.pushUserAction({
        category: 'bulk_actions',
        action: actionType as BulkActionType,
        metadata: {
          device_count: event.analyticsMetadata.device_count,
          ...(event.analyticsMetadata.fields_updated && {
            fields_updated: event.analyticsMetadata.fields_updated
          }),
        },
      });
    }

    const deviceIds = updates.map((u) =>
      String((u as { deviceId: unknown }).deviceId)
    );

    // Activate the SSRM selection lock for this grid session so checkboxes
    // disable instantly and all selected rows show the spinner via the
    // lock-derived predicate path. Also persists to sessionStorage via the
    // persistLockToSessionStorage$ effect so same-tab refresh restores the
    // lock pessimistically. Releases when Firestore terminal state arrives,
    // when the user clicks Cancel, or when reconcileStuckJobs detects the
    // job has actually finished (case A/C from the failsafe design).
    const gridSessionUuid = this.currentGridSessionId();
    if (gridSessionUuid) {
      this.store.dispatch(
        bulkJobEnqueued({
          jobId,
          gridSessionUuid,
          selectedCount: updates.length,
        })
      );
    }

    // Legacy behavior preserved during the transition: also mark rows via
    // the per-id Set so consumers reading `updatingIds$` directly (e.g.
    // unit tests, Storybook) keep working. The lock-derived predicate path
    // makes this redundant for the live grid (selected rows would get the
    // spinner from the predicate alone), but removing it requires updating
    // every test fixture that asserts on markRowsAsUpdating side effects —
    // out of scope for this PR.
    this.markRowsAsUpdating(deviceIds);
    const shouldRefreshGrid = !command || command === 'SYNC_LAST_USER_OU';

    const customerIdNum = parseInt(this.customerId, 10);
    if (!Number.isFinite(customerIdNum)) {
      // No customer context — can't monitor. Clear spinners so they
      // don't get stuck; the user can manually refresh if needed.
      this.clearRowsUpdating(deviceIds);
      return;
    }

    // Monitor the bulk-action job. On success, full grid refresh via
    // purgeAndRefresh (matches the browser-refresh path the user
    // verified works). Two prior attempts at surgical update via
    // applyServerSideTransaction failed visually in sandbox — the
    // network fetch returned correct data both times but cells stayed
    // on old values until manual browser refresh. Switching to full
    // refresh trades page/scroll position preservation for reliable
    // visual update; for bulk actions (which already shift the user's
    // mental context) this is the right tradeoff. The in-cell
    // save keeps the surgical path because preserving scroll matters
    // there.
    //
    // For commands that don't change visible field data (WIPE_USERS,
    // REMOTE_POWERWASH) we skip the refresh but still clear spinners
    // on completion so the user gets a clean visual signal.
    this.jobMonitorService
      .monitorWithCallbacks(
        customerIdNum,
        jobId,
        () => {
          if (shouldRefreshGrid) {
            this.deviceSearchGrid?.purgeAndRefresh();
          }
          this.clearRowsUpdating(deviceIds);
        },
        () => {
          this.clearRowsUpdating(deviceIds);
          this.snackbarService.showError(
            'Bulk action failed',
            'Close',
            { horizontalPosition: 'center', duration: 5000 }
          );
        }
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
   * Bound to the bulk-update drawer's `(exportSkippedDevices)` event
   * (added by PR #880 EDUS-2163). Delegates to `DeviceExportImportService`
   * — the service owns dialog plumbing across the device-search component
   * (export, export-for-bulk-edit, import); the skipped-devices export is
   * the same shape and lives there for locality.
   */
  public onExportSkippedDevices(event: {
    skipGroups: BulkActionSkipGroup<DeviceAsset>[];
    actionType: string;
  }): void {
    this.deviceExportImport.openExportSkippedDevicesDialog({
      skipGroups: event.skipGroups,
      actionType: event.actionType,
      timezone: this.currentDateTimeSettings.timezone,
      identity: this.getIdentity(),
      userName: this.userName,
    });
  }

  /**
   * open the bulk-edit drawer with optional action-specific
   * config (visibleFields, defaultValues, actionType). Pre-fetches full
   * device data via `getDevicesByIds` ONLY when the action involves
   * Append/Prepend operations on text fields — those operators need the
   * existing field value to compute the new value, so the drawer must
   * have full row data for validation. OU/status/etc. operators don't
   * need pre-fetched data.
   *
   * After PR #880 (EDUS-2163) the eligibility-action types
   * (enable/disable/deprovision/profile-wipe/factory-reset/move-ou) also
   * need device data — the eligibility evaluator gates on status,
   * osVersion, autoUpdateExpiration, orgUnitPath. Pre-fetch covers both.
   *
   * Wired to the navbar's `openBulkEditDrawer` callback.
   */
  public openBulkEditDrawer(actionConfig?: {
    visibleFields?: string[];
    defaultValues?: Array<{
      fieldName: string;
      updateType: string;
      value: unknown;
    }>;
    actionType?: string;
  }): void {
    // SSRM selection-lock failsafe gate: when a bulk job is in flight for
    // this grid session, the user's selection is frozen and a second bulk
    // action would either operate on the same locked set (duplicate work)
    // or race against the in-flight job's snapshot. Block here at the
    // gateway and surface a snackbar so the user understands why the
    // drawer didn't open.
    //
    // TODO follow-up: also disable the navbar bulk-action buttons (which
    // call this method) so the affordance is visually clear, not just
    // functionally blocked. That's a navbar-component change beyond this
    // PR's scope; the snackbar is the bridge until then.
    if (this.isLockActiveSnapshot()) {
      this.snackbarService.showMessage(
        'Wait for current job to complete before starting a new bulk action.',
        '',
        { duration: 5000 }
      );
      return;
    }
    this.bulkEditActionConfig = actionConfig;

    // Fetch device data for:
    // - Append/Prepend text-field validation (edit-import + text fields)
    // - Eligibility evaluation (EDUS-2163): every action that gates on device
    //   state needs status / osVersion / autoUpdateExpiration / orgUnitPath
    const eligibilityActionTypes = [
      'enable',
      'disable',
      'deprovision',
      'profile-wipe',
      'factory-reset',
      'move-ou',
    ];
    const needsDeviceData =
      actionConfig?.actionType === 'edit-import' ||
      (actionConfig?.actionType !== undefined &&
        eligibilityActionTypes.includes(actionConfig.actionType)) ||
      actionConfig?.visibleFields?.some((field) =>
        ['notes', 'annotatedAssetId', 'annotatedUser', 'annotatedLocation'].includes(
          field
        )
      );
    if (needsDeviceData && this.selectedDeviceIds.length > 0) {
      this.devicesDataService
        .getDevicesByIds(this.selectedDeviceIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (devices) => {
            this.selectedDeviceData = devices.filter(
              (d): d is DeviceAsset => d != null
            );
            this.bulkUpdateActionComponent?.openDrawer();
          },
          error: () => {
            // Validation will skip duplicate-detection if data is missing,
            // but the drawer should still open so the user can dispatch.
            this.selectedDeviceData = [];
            this.bulkUpdateActionComponent?.openDrawer();
          },
        });
    } else {
      this.selectedDeviceData = [];
      this.bulkUpdateActionComponent?.openDrawer();
    }
  }

  onCellClicked(event: CellClickedEvent): void {
    const colId = event.column?.getColId();
    if (colId !== 'previewIconComponent') return;
    const deviceData = event.data as { id?: string | number } | undefined;
    if (deviceData?.id == null) return;
    const currentPage = this.deviceSearchGrid?.getCurrentPage() ?? 0;
    sessionStorage.setItem('deviceSearchReturnPage', currentPage.toString());
    this.router.navigate(['/device-summary', deviceData.id]);
  }

  /**
   * Bound to the SSRM grid's `(dataLoaded)` event,
   * which now emits `{itemTotal, hasStaleData?}` . Reads
   * itemTotal from the event payload directly instead of querying the grid
   * after the fact (the previous implementation worked too but was
   * race-prone — the grid's pagination row count isn't always settled by
   * the moment dataLoaded fires).
   *
   * The navbar action disabled-state update + wipe-profiles auto-trigger
   * that lived here previously were removed  and re-land in
   * the bulk-action drawer.
   */
  onDataLoaded(event?: { itemTotal: number; hasStaleData?: boolean }): void {
    this.isLoading = false;
    this.deviceGridStateService.setItemTotal(event?.itemTotal ?? 0);
    // gate the Wipe Profiles workflow auto-trigger on the
    // first dataLoaded fire after the matching filter set is detected.
    // The one-shot guard inside makes subsequent dataLoaded fires
    // (pagination, refresh) a no-op for the same session.
    this.executeWipeProfilesWorkflow();
  }

  /**
   * cross-page selection round-trip. The SSRM grid emits the new
   * full selection set on every user-driven checkbox click; we mirror it
   * to the state service (which other navbar/toolbar components observe
   * via `selectedCount$` / `selectedIds$`). The state service's emission
   * propagates back through `selectedIdSet$` → grid's `[selectedIds$]` →
   * `syncSelectionState` (silent mirror), closing the loop without
   * infinite emissions.
   *
   * Setting both selectedIds and selectedCount keeps existing consumers
   * of `selectedCount$` (e.g. the navbar action enable/disable logic
   * that returns later) working without requiring them to derive
   * the count from selectedIds.length.
   */
  /**
   * Phase 9 of EDUS-2485: legacy `(selectionChanged)` Output is no longer
   * fired by the grid library (see Phase 7 deprecation). Kept for compile
   * compat in case any code path still binds it; the grid will never call
   * this handler.
   */
  onSelectionChanged(_selected: Set<string>): void {
    // intentionally empty — see method docstring
  }

  /**
   * Per-row checkbox toggle handler bound to the grid's new
   * `(selectionToggled)` Output.
   *
   * Two things happen on every toggle:
   *  1. **Optimistic count bump** — apply +1/-1 immediately so the footer
   *     and select-all banner appear/update on the same frame as the
   *     checkbox. Without this the user waits the 250ms bufferTime + the
   *     server roundtrip before any UI feedback (count stays at 0).
   *     Clamped at 0 to defend against any deselect storm that arrives
   *     before an in-flight select reconciliation.
   *  2. **Push into the batch stream** — the bufferTime(250) handler in
   *     ngOnInit picks it up, groups by op, fires the server call, and
   *     overwrites the optimistic count with the authoritative
   *     `res.count`. So any prediction drift self-corrects within ~one
   *     network roundtrip.
   */
  onSelectionToggled(event: { rowId: string; op: 'select' | 'deselect' }): void {
    const current = this.deviceGridStateService.getSelectedCount();
    const optimistic =
      event.op === 'select' ? current + 1 : Math.max(0, current - 1);
    this.deviceGridStateService.setSelectedCount(optimistic);
    this.selectionToggleEvents$.next(event);
  }

  /**
   * Server-side selection count handler bound to the grid's new
   * `(selectionCount)` Output. Mirrors the count into the device-grid
   * state service so the footer + banner re-render. Also handles the
   * "selection went to 0" auto-clear of `showSelectedOnly` (without it
   * the grid would stay filtered on an empty selection set forever).
   */
  onSelectionCountChange(count: number): void {
    this.deviceGridStateService.setSelectedCount(count);
    if (this.showSelectedOnly && count === 0) {
      this.showSelectedOnly = false;
    }
  }

  /**
   * Centralized error surface for the 4 selection-write paths
   * (selectByIds/deselectByIds/selectByFilter/deselectByFilter via the
   * batch handler). Element-cap rejections (HTTP 400) carry the
   * actionable backend message; transport failures get a generic copy.
   * On any failure we also re-fetch the canonical count from the server
   * so the footer reconciles with reality.
   */
  private handleSelectionWriteError(err: unknown): void {
    const message =
      (err as { error?: { message?: string } })?.error?.message ??
      (err as Error)?.message ??
      'Selection update failed — please retry.';
    this.snackbarService.showError(message, 'Close', {
      horizontalPosition: 'center',
      duration: 5000,
    });
    // Reconcile footer count from server truth.
    const sessionId = this.currentGridSessionId();
    if (sessionId) {
      this.devicesDataService
        .getSelectionCount(sessionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) =>
            this.deviceGridStateService.setSelectedCount(res.count),
          error: () => {
            // Reconcile failed too — leave the optimistic count in place.
          },
        });
    }
  }

  /**
   * Stub for the route-guard contract — always allows navigation. The
   * SSRM in-cell edit flow keeps pending edits across pagination
   * (cellChanges Map is consumer-owned and survives block evict), so
   * pagination doesn't need a confirm dialog. Route-level navigation
   * (leaving the page entirely) with pending edits is a deferred UX
   * concern — current behavior is to silently lose edits on route
   * change, same as the legacy app.
   */
  confirmNavigationWithUnsavedChanges(): Observable<boolean> | boolean {
    return true;
  }

  // ===  in-cell edit handlers ===

  /**
   *  SSRM grid's `(cellValueChanged)` emits when the user commits
   * an edit (Enter / Tab / blur). We track the new value in `cellChanges`
   * keyed by row id + field path. The grid library separately tracks the
   * touched cell internally to drive the modified-cell visual.
   *
   * No-op when the new value equals the old (the SSRM grid guards against
   * spurious blur emissions, but defensive guard here too in case a
   * different consumer of the SSRM grid lacks that filter).
   */
  /**
   *  SSRM grid forwards AG-Grid's
   * `cellEditingStarted` so we can flip Cancel/Save bar visibility on
   * cell-editor open (rather than waiting for a value commit). Mirrors
   * the user mental model: "I'm editing this device → show me the
   * actions to commit/cancel."
   */
  onCellEditingStarted(): void {
    this.isEditingActive = true;
  }

  /**
   *  pair to onCellEditingStarted. Cancel/Save
   * bar stays visible if there are pending changes (editChangeCount > 0)
   * even after the editor closes — the bar's *ngIf is the OR of these
   * two flags.
   */
  onCellEditingStopped(): void {
    this.isEditingActive = false;
  }

  onCellValueChanged(event: CellValueChangedEvent): void {
    if (event.newValue === event.oldValue) return;
    const data = event.data as { id?: string | number } | undefined;
    const field = event.colDef.field;
    if (data?.id == null || !field) return;
    const rowId = String(data.id);
    let fields = this.cellChanges.get(rowId);
    if (!fields) {
      fields = new Map();
      this.cellChanges.set(rowId, fields);
    }
    fields.set(field, event.newValue);
    this.editChangeCount = this.computeChangeCount();
    // push fresh snapshot to the SSRM grid library so its
    // factory's pendingEdits overlay sees the new value on the next
    // block fetch. Subscribers see a NEW Map reference each emit so
    // distinctUntilChanged-style consumers detect the change.
    this.cellChanges$.next(this.snapshotCellChanges());
    // If "show changes only" is on, the allow-list now includes a row
    // that wasn't there before — refresh it so the new edit's row is
    // visible in the filtered set.
    if (this.showChangesOnly) {
      this.idAllowList$.next(Array.from(this.cellChanges.keys()));
    }
  }

  /**
   * Bound to the SSRM grid's `(cellUndoClicked)` Output. Fires when the
   * user clicks the per-cell undo overlay button on an edited cell.
   *
   * The grid library has already reverted `node.data` to the original
   * value AND dropped its own internal `modifiedCellsByRow` entry by the
   * time this fires — this handler is responsible only for the consumer-
   * owned state mirror:
   *
   *   - Drop the matching entry from `cellChanges` (the in-flight save
   *     payload). If the row's inner Map empties out, prune the row key
   *     entirely so `cellChanges.keys()` no longer includes it.
   *   - Recompute `editChangeCount` so the "Save Changes (N)" badge in
   *     the edit-bar reflects the undo immediately.
   *   - Push a fresh `cellChanges$` snapshot so the grid library's
   *     `pendingEdits$` overlay drops the reverted value from any future
   *     block evict + refetch (otherwise an evicted row coming back
   *     would re-overlay the new value the user just undid).
   *   - When "Show changes only" is on AND this undo emptied the row's
   *     edit set, the row should drop out of the filtered view —
   *     recompute the allow-list from the current cellChanges keys.
   *     Mirror of the symmetric add in `onCellValueChanged` above.
   */
  onCellUndoClicked(event: { rowId: string; field: string }): void {
    const { rowId, field } = event;
    const fields = this.cellChanges.get(rowId);
    if (!fields?.has(field)) return;
    fields.delete(field);
    if (fields.size === 0) {
      this.cellChanges.delete(rowId);
    }
    this.editChangeCount = this.computeChangeCount();
    this.cellChanges$.next(this.snapshotCellChanges());
    if (this.showChangesOnly) {
      if (this.cellChanges.size === 0) {
        // Undoing the last pending edit while the toggle is on:
        // the allow-list would emit `[]`, which the grid library
        // short-circuits to "no filter". That visually drops the
        // filter, but `showChangesOnly` stayed true — so the very
        // next cellValueChanged would snap the grid back into the
        // filtered view (just that one new row). Reset the toggle
        // state in lock-step with the empty change set, mirroring
        // `clearCellChanges()` semantics: filter is no longer
        // meaningful when there are zero pending edits, so exit
        // "Show changes only" mode entirely.
        this.showChangesOnly = false;
        this.idAllowList$.next(undefined);
      } else {
        this.idAllowList$.next(Array.from(this.cellChanges.keys()));
      }
    }
  }

  /**
   * toggle the "Show changes only" filter. When ON, emits the
   * current cellChanges keys to `idAllowList$` so the SSRM grid filters
   * server-side to only those rows. When OFF, emits undefined to restore
   * the normal filter chain.
   *
   * Cancels any open editor first (no commit) so a click on the toggle
   * doesn't accidentally commit a half-typed value before the refresh
   * fires.
   *
   * Activating this turns off `showSelectedOnly` — both toggles write
   * the same `idAllowList$` Subject, so they're mutually exclusive
   * (last toggle wins).
   */
  toggleShowChangesOnly(): void {
    // Commit any open editor first so the user doesn't lose a half-typed
    // value when the toggle's refreshServerSide kicks in. cellValueChanged
    // fires synchronously inside stopEditing(false), so the new value
    // lands in cellChanges + cellChanges$ before we compute the allow-list.
    this.deviceSearchGrid?.commitOpenEditor();
    this.showChangesOnly = !this.showChangesOnly;
    if (this.showChangesOnly) this.showSelectedOnly = false;
    this.idAllowList$.next(
      this.showChangesOnly ? Array.from(this.cellChanges.keys()) : undefined
    );
  }

  /**
   * Footer "Show Selected Only" toggle handler. Snapshots the current
   * selection into `idAllowList$` so the SSRM grid filters server-side
   * to those IDs. Snapshot semantics (not live-tracking) — if the user
   * adds or removes selections after activating, the filtered view stays
   * frozen; the auto-clear in the `selectedIds$` subscription handles
   * the "selection went to 0" edge case so the grid doesn't orphan on a
   * stale ID list.
   *
   * Activating this turns off `showChangesOnly` for mutual exclusion
   * (both toggles write the same `idAllowList$` Subject).
   */
  onShowSelectedOnlyChange(next: boolean): void {
    this.showSelectedOnly = next;
    if (next) {
      this.showChangesOnly = false;
    }
    // Phase 9 of EDUS-2485: idAllowList$.next(...) call removed. The grid
    // now reads `[useSelectionAsFilter]="showSelectedOnly"` directly and
    // the data service translates that to a query param; the backend
    // filters server-side using the Redis selection set instead of the
    // client snapshotting selection IDs into a URL.
  }

  onGridLoadError(error: Error): void {
    this.snackbarService.showError(
      error?.message
        ? `Failed to load devices: ${error.message}`
        : 'Failed to load devices.'
    );
  }

  /**
   *  bulk save in-cell edits as a single update job. Builds the
   * `_updates` payload with both `id` (grid row identity) and `deviceId`
   * (audit-log identity for enrichment), tagged as
   * `JobTypes.IN_CELL_EDIT` so the jobs panel categorizes correctly.
   *
   * Per architecture decision Q2 = B:
   *   - the existing machinery handles the post-job grid update:
   *     `markRowsAsUpdating` → backend job runs → `fetchAndUpdateRows`
   *     surgically applies fresh row data via `applyRowUpdates`.
   *   - Audit-log enrichment (`handleActionsExecuted`) is deferred to
   *     Saves succeed end-to-end without the audit
   *     trail; The audit-log wires back in alongside bulk-action callbacks.
   *
   * On dispatch failure (network error → null response from
   * `updateDevices`), surface a snackbar and stay in edit mode so the
   * user can retry without losing their pending changes.
   */
  onSaveChanges(): void {
    if (this.isSaving) return;
    // Commit any in-flight editor BEFORE checking cellChanges — when the
    // user clicks Save while still typing in a cell (the very flow that
    // `isEditingDirty` enables), the value hasn't reached cellChanges
    // yet via the post-commit `cellValueChanged` path. stopEditing(false)
    // synchronously fires `cellValueChanged`, which lands the typed
    // value in cellChanges before the size check below.
    this.deviceSearchGrid?.commitOpenEditor();
    if (this.cellChanges.size === 0) return;

    const _updates = Array.from(this.cellChanges.entries()).map(
      ([deviceId, changes]) => {
        const flat: Record<string, unknown> = {
          id: deviceId,
          deviceId,
        };
        for (const [field, value] of changes) flat[field] = value;
        return flat;
      }
    );
    const editedDeviceIds = _updates.map((u) => String(u.deviceId));

    // Snapshot two things from cellChanges BEFORE mutating it in the
    // job-started callback:
    //   * savingCellsSnapshot: row→field set, drives the per-cell saving
    //     spinner via savingCells$.
    //   * originalCellChanges: row→field→value, used to (a) tell Stage 1
    //     which keys to drop (preserves any NEW edits the user makes
    //     during the Save→started dispatch window) and (b) restore the
    //     edits on Stage 2 hard failure so the user can retry without
    //     retyping.
    const savingCellsSnapshot = new Map<string, Set<string>>();
    const originalCellChanges = new Map<string, Map<string, unknown>>();
    for (const [rowId, fields] of this.cellChanges) {
      savingCellsSnapshot.set(rowId, new Set(fields.keys()));
      originalCellChanges.set(rowId, new Map(fields));
    }

    // Track in-cell editing for product analytics
    const editedFields = new Set<string>();
    let totalCellsEdited = 0;
    this.cellChanges.forEach((fields) => {
      totalCellsEdited += fields.size;  // Count actual cells, not rows
      fields.forEach((_, fieldName) => editedFields.add(fieldName));
    });
    // cell_count tracks the number of individual cells edited (not device count).
    // For example: editing Notes on 3 devices = 3 cells. Editing Notes + Asset ID
    // on 2 devices = 4 cells total. This matches the spec requirement to track
    // "# of devices affected" at cell granularity.
    this.gtmService.pushInCellEditAction('cells_saved', {
      field_name: Array.from(editedFields).join(','),
      cell_count: totalCellsEdited,
    });

    this.setIsSaving(true);

    this.devicesDataService
      .updateDevices(_updates, JobTypes.IN_CELL_EDIT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result?.success) {
          this.setIsSaving(false);
          this.snackbarService.showError(
            'Failed to save device edits. Please try again.',
            'Close',
            { horizontalPosition: 'center', duration: 5000 }
          );
          return;
        }

        this.snackbarService.showMessage(
          `Successfully began job to update ${_updates.length} ${
            _updates.length === 1 ? 'device' : 'devices'
          }.`,
          'Close',
          { horizontalPosition: 'center', duration: 3000 }
        );

        const customerIdNum = parseInt(this.customerId, 10);
        if (!Number.isFinite(customerIdNum)) {
          // No customer context — can't monitor. Best-effort cleanup.
          this.setIsSaving(false);
          return;
        }

        // Stage 1: monitor for "job started" → swap UI from
        // Saving-button-bar to per-cell saving spinners. The spinners
        // continue showing until the job finishes (Stage 2). Drop
        // pending edits but DO NOT touch `showChangesOnly`/`idAllowList$`
        // — the user wants to watch the spinners on their filtered
        // set through completion. Toggle clears at Stage 2 success.
        this.jobMonitorService
          .monitorJobStarted(customerIdNum, result.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.setIsSaving(false);
              this.isEditingActive = false;
              this.savingCells$.next(savingCellsSnapshot);
              // Drop only the rows that were dispatched. Edits the user
              // made on OTHER rows during the Save→started window are
              // preserved (the original unconditional clear() silently
              // wiped them). For rows in originalCellChanges, the user
              // can't have a meaningful in-flight edit anyway because
              // those cells are gated non-editable by savingCells$.
              for (const rowId of originalCellChanges.keys()) {
                this.cellChanges.delete(rowId);
              }
              // Brian's PR #885 review #4: cellChanges is Map<rowId, Map<field, value>>;
              // .size returns row count, not the per-cell count needed for the
              // "Save Changes (N)" badge. Use computeChangeCount() helper instead.
              this.editChangeCount = this.computeChangeCount();
              this.cellChanges$.next(new Map(this.cellChanges));
              this.deviceSearchGrid?.exitEditMode();
            },
            error: (error: unknown) => {
              // Job dispatched but Firestore never reported it picking up
              // the work within the timeout window. Recover the UI so the
              // user isn't stuck on "Saving…" forever; pending edits stay
              // intact so they can retry.
              this.setIsSaving(false);
              const message =
                error instanceof TimeoutError
                  ? 'Save dispatched but status unknown — check the Jobs panel.'
                  : 'Save monitor failed unexpectedly — check the Jobs panel.';
              this.snackbarService.showError(message, 'Close', {
                horizontalPosition: 'center',
                duration: 5000,
              });
            },
          });

        // Stage 2: monitor for completion → clear spinners + refresh.
        // Chains through refreshRows() (the existing machinery) so the
        // saas-device-api asset cache gets explicitly busted before
        // the refetch — bulk-edit jobs themselves don't invalidate
        // cache, so a direct getFullDevicesByIds would hit stale data.
        this.jobMonitorService
          .monitorWithCallbacks(
            customerIdNum,
            result.id,
            () => {
              // Stage 2 success: clear the saving spinners, drop the
              // "show changes only" filter (so the refresh restores the
              // full filtered view), and trigger the cache-bust + refetch.
              this.savingCells$.next(new Map());
              this.showChangesOnly = false;
              this.idAllowList$.next(undefined);
              this.deviceGridStateService.refreshRows(editedDeviceIds);
            },
            (error?: unknown) => {
              // Drop saving spinners (job is no longer in flight).
              this.savingCells$.next(new Map());

              if (error instanceof TimeoutError) {
                // Job MAY still be running — don't restore edits since
                // we can't know if the save actually applied. Surface a
                // distinct message so the user knows to check the Jobs
                // panel rather than retry blindly.
                this.snackbarService.showError(
                  'Device update job timed out — check the Jobs panel.',
                  'Close',
                  { horizontalPosition: 'center', duration: 5000 }
                );
                return;
              }

              // Hard failure (failed/error/cancelled) — the job didn't
              // succeed, so restore the original edits so the user can
              // retry without retyping. Newer in-flight edits to other
              // rows take precedence; if the user re-edited the same
              // row, their newer values win the merge.
              for (const [rowId, fields] of originalCellChanges) {
                const existing = this.cellChanges.get(rowId);
                const merged = new Map(fields);
                if (existing) {
                  for (const [field, value] of existing) {
                    merged.set(field, value);
                  }
                }
                this.cellChanges.set(rowId, merged);
              }
              // Brian's PR #885 review #4: cellChanges is Map<rowId, Map<field, value>>;
              // .size returns row count, not the per-cell count needed for the
              // "Save Changes (N)" badge. Use computeChangeCount() helper instead.
              this.editChangeCount = this.computeChangeCount();
              this.cellChanges$.next(new Map(this.cellChanges));
              this.snackbarService.showError(
                'Device update job failed — your edits are preserved.',
                'Close',
                { horizontalPosition: 'center', duration: 5000 }
              );
            }
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      });
  }

  /**
   *  discard in-cell edits with a confirmation dialog (legacy UX
   * preserved). On confirm: clear local map + exit edit mode + force a
   * grid refresh so AG-Grid drops the in-memory edited values that
   * mutated `node.data` during inline editing. Discard is the rare path
   * — the network cost of `purgeAndRefresh` (one block fetch) is
   * acceptable for guaranteed correctness.
   */
  onDiscardChanges(): void {
    if (this.cellChanges.size === 0) {
      // No pending edits — discard collapses to a plain exit. Reset
      // isEditingActive explicitly because AG-Grid's
      // `cellEditingStopped` event timing can't be relied on when the
      // click happens on a button outside the grid (no pending value
      // commit means the editor close path doesn't always fire). The
      // library's exitEditMode() now also calls gridApi.stopEditing(true)
      // to cancel any half-open editor.
      this.isEditingActive = false;
      this.deviceSearchGrid?.exitEditMode();
      return;
    }
    const dialogData: CdwConfirmationDialogData = {
      title: 'Unsaved Changes',
      message:
        'WARNING! There are unsaved changes that will be lost if you continue.',
      type: 'warning',
      size: 'medium',
      layout: 'warning-box',
      fontFamily: 'Roboto',
      confirmText: 'Revert Changes',
      cancelText: 'Go Back',
      confirmButtonColor: 'warn',
      showCancelButton: true,
      hard: true,
    };
    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: { confirmed: boolean } | undefined) => {
        if (!result?.confirmed) return;
        // Same reasoning as the no-edits branch above: explicit
        // isEditingActive reset so the bar's *ngIf flips false.
        this.isEditingActive = false;
        this.clearCellChanges();
        this.deviceSearchGrid?.exitEditMode();
        // Force a re-fetch so the grid drops the edited values that AG-Grid
        // committed to node.data during inline-edit.
        this.deviceSearchGrid?.purgeAndRefresh();
        this.snackbarService.showMessage(
          'The data has been restored.',
          'Close',
          { horizontalPosition: 'center', duration: 3000 }
        );
      });
  }

  /**
   *  clear all pending cell edits and reset the change-count
   * counter. Internal helper used by save-success, discard-confirm, filter-
   * change reset, customer-change reset, and ngOnDestroy. Distinct from
   * `exitEditMode` — clearing the map and exiting edit mode are separate
   * operations (save flow does both; entering edit mode after a previous
   * save already cleared the map should not toggle edit mode).
   */
  /**
   * rebuild the bulk-edit drawer's field-config list using the
   * currently-loaded OU tree. Called once after `getOrgUnits()` resolves
   * (or errors with empty fallback) so the drawer's OU-picker dropdown
   * has options to render. Pure delegation to the existing
   * `buildBulkEditFieldConfigs()` helper from `./configs/bulk-edit-config`
   * — keeps the field/operator/template definitions in one place.
   */
  private initializeBulkEditConfigs(): void {
    this.bulkUpdateFieldConfigs = buildBulkEditFieldConfigs(this.ouTreeData);
  }

  /**
   * detect when the active filter set matches the Wipe
   * Profiles workflow's expected pattern (single chip on
   * `vendorProperties.google.diskUsedCategory` containing both 80 and
   * 90 buckets) AND the navigation arrived with `workflowType ===
   * 'profile-wipe'`. The two-condition gate prevents accidental
   * triggers when the same filter is applied via URL share or manual
   * filter chips on a normal page load.
   *
   * Sets `wipeProfilesFilterActive` only — does NOT trigger the
   * workflow itself. Trigger is gated on the next `dataLoaded` fire
   * (so we know the grid has rows to act on).
   */
  private checkForWipeProfilesWorkflow(filters: SelectedFilterField[]): void {
    if (this.wipeProfilesWorkflowTriggered) return;
    if (filters.length !== 1) {
      this.setWipeProfilesFilterActive(false);
      return;
    }
    const diskUsedFilter = filters.find(
      (f) => f.field.name === 'vendorProperties.google.diskUsedCategory'
    );
    if (!diskUsedFilter) {
      this.setWipeProfilesFilterActive(false);
      return;
    }
    try {
      const filterValues: unknown = JSON.parse(
        diskUsedFilter.matchValue as string
      );
      const isWipeProfilesFilter =
        Array.isArray(filterValues) &&
        filterValues.length === 2 &&
        filterValues.includes(DeviceDiskUsed.Eighty) &&
        filterValues.includes(DeviceDiskUsed.Ninety);
      if (!isWipeProfilesFilter) {
        this.setWipeProfilesFilterActive(false);
        return;
      }
      // Only activate the workflow if the navigation explicitly carried
      // the profile-wipe intent. Prevents triggering on a page refresh
      // where the user already has these filters chipped from a prior
      // session — without this guard, F5 would re-open the confirmation.
      if (this.currentWorkflowType !== 'profile-wipe') {
        this.setWipeProfilesFilterActive(false);
        return;
      }
      this.setWipeProfilesFilterActive(true);
    } catch {
      this.setWipeProfilesFilterActive(false);
    }
  }

  // Centralizes the filter-active flag and clears the one-shot trigger
  // when the filter goes inactive. Prevents the previous bug where each
  // dataLoaded fire (one per block fetch / paginate) re-triggered the
  // workflow because the trigger flag was reset on empty/error paths.
  // Now the trigger only resets on a filter-edge transition.
  private setWipeProfilesFilterActive(active: boolean): void {
    if (this.wipeProfilesFilterActive && !active) {
      this.wipeProfilesWorkflowTriggered = false;
    }
    this.wipeProfilesFilterActive = active;
  }

  /**
   * execute the Wipe Profiles workflow once the grid has
   * loaded matching data. Called from `onDataLoaded` (the SSRM grid's
   * dataLoaded event fires per block load — the legacy used the same
   * hook). The one-shot guard `wipeProfilesWorkflowTriggered` makes
   * subsequent dataLoaded fires within the session a no-op.
   *
   * Per audit Q5 (locked): empty result set is a silent skip — no
   * confusing "no devices" UX. The workflow either has devices to
   * wipe or it doesn't run.
   *
   * Flow: fetchAllDeviceIds (helper) → push to selection state
   * service → invoke navbar's existing `showProfileWipeConfirmation`
   * which opens the confirmation dialog and dispatches the
   * WIPE_USERS command on confirm.
   */
  private executeWipeProfilesWorkflow(): void {
    if (!this.wipeProfilesFilterActive || this.wipeProfilesWorkflowTriggered) {
      return;
    }
    // One-shot per filter-active edge. Pagination fires dataLoaded once
    // per block; resetting this flag on the empty/error paths caused the
    // workflow to re-trigger on every page until something succeeded.
    // Recovery now requires a filter change (which clears the flag via
    // setWipeProfilesFilterActive).
    this.wipeProfilesWorkflowTriggered = true;
    if (this.deviceGridStateService.getItemTotal() === 0) {
      // Empty result — silent skip per locked audit Q5.
      return;
    }
    if (!this.fetchAllDeviceIds) {
      return;
    }
    this.fetchAllDeviceIds()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (allIds: string[]) => {
          if (!allIds || allIds.length === 0) return;
          this.deviceGridStateService.setSelectedIds(allIds);
          this.deviceGridStateService.setSelectedCount(allIds.length);
          // PR #880 EDUS-2163: profile-wipe now routes through the
          // bulk-edit drawer (with eligibility evaluation + warning
          // archetype) instead of the standalone confirmation dialog.
          this.openBulkEditDrawer(getBulkEditConfigForAction('profile-wipe'));
        },
        error: () => {
          // Silent — the workflow won't auto-retry within this filter
          // session. The user can refresh or change filters to re-arm.
        },
      });
  }

  /**
   * setter for `isSaving` that ALSO triggers a navbar
   * disabled-state re-evaluation. The navbar's bulk-action buttons
   * gate on `isSaving` (locked Q2: block bulk dispatch during in-cell
   * save window) but the navbar reads its predicate at registration
   * time — without an explicit re-registration it wouldn't pick up
   * the change. Centralizing the assignment in one method ensures
   * the trigger never gets forgotten across the four sites that
   * toggle isSaving (Save click, dispatch failure, no-customer-id
   * early-return, job-started callback).
   */
  private setIsSaving(value: boolean): void {
    this.isSaving = value;
    this.navbarButtonsRegistry.updateDeviceActionsDisabledState();
  }

  // Identity bundle for DeviceExportImportService calls. Centralizes the
  // String/parseInt coercions so the three call sites stay one-liners and
  // any future identity-shape change happens in one place.
  private getIdentity(): {
    fbUserId: string;
    dbUserId: number;
    userEmail: string;
    userName?: string;
    customerId: number;
  } {
    return {
      fbUserId: String(this.fbUserId),
      dbUserId: this.dbUserId,
      userEmail: this.userEmail,
      // EDUS-2520: forwarded into the export email template's display name.
      // Without this, the share-export step falls back to userEmail and the
      // user sees "<email> shared an export with you" / "Created by: <email>
      // <email>" instead of the human-readable name.
      userName: this.userName || undefined,
      customerId: parseInt(this.customerId, 10),
    };
  }

  private clearCellChanges(): void {
    this.cellChanges.clear();
    this.editChangeCount = 0;
    // any "show changes only" filter is no longer meaningful
    // when the change set is empty — clear it BEFORE emitting the new
    // cellChanges snapshot so the library sees a coherent state.
    this.showChangesOnly = false;
    this.idAllowList$.next(undefined);
    this.cellChanges$.next(new Map());
  }

  /**
   * build a fresh defensive-copy snapshot of `cellChanges` for
   * emission to `cellChanges$`. Each entry's inner Map is shallow-cloned
   * so subscribers can hold the snapshot without seeing later mutations
   * leak through (defensive against the Map's mutable nature). Cheap —
   * cellChanges is human-scale (typically <100 rows, <500 fields total).
   */
  private snapshotCellChanges(): Map<string, Map<string, unknown>> {
    const snap = new Map<string, Map<string, unknown>>();
    for (const [rowId, fields] of this.cellChanges) {
      snap.set(rowId, new Map(fields));
    }
    return snap;
  }

  /**
   *  derive the change count from the current `cellChanges` Map.
   * Sum of inner Map sizes (number of distinct field-edits across all
   * rows). Recomputed on every cell-value-change so the toolbar Save
   * button label stays in sync.
   */
  private computeChangeCount(): number {
    let count = 0;
    for (const fields of this.cellChanges.values()) count += fields.size;
    return count;
  }

  /**
   * mark rows as currently updating. The visible-row spinner +
   * stale-row CSS are driven by the SSRM grid's `[updatingIds$]`
   * augmentation, which subscribes to the state service's
   * `updatingIds$` observable. No grid-API touching here — just update
   * the external set, the grid handles the rest.
   */
  private markRowsAsUpdating(ids: string[]): void {
    this.deviceGridStateService.markAsUpdating(ids);
  }

  /**
   * clear updating state for specific rows.
   * See `markRowsAsUpdating` — same pattern, grid auto-redraws.
   */
  private clearRowsUpdating(ids: string[]): void {
    this.deviceGridStateService.clearUpdating(ids);
  }

  /**
   * refetch the latest device records and surgically apply them
   * via the SSRM grid's `applyRowUpdates` (`applyServerSideTransaction`).
   * Evicted rows are silently skipped — they fetch fresh on the next
   * paginate, and the asset cache will return non-stale data since the
   * job just completed. Updating state is cleared regardless of how many
   * rows were actually updated in the loaded blocks.
   *
   * `getFullDevicesByIds` does NOT pass `selectFields`, so the backend
   * returns the full row shape (backwards-compat: omitted
   * selectFields = no projection). The full shape is required so we
   * don't accidentally degrade data already in the loaded block.
   */
  private fetchAndUpdateRows(deviceIds: string[]): void {
    this.devicesDataService
      .getFullDevicesByIds(deviceIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (devices) => {
          const validDevices = devices.filter(
            (d): d is DeviceAsset => d != null
          );
          this.deviceSearchGrid?.applyRowUpdates(validDevices);
          this.clearRowsUpdating(deviceIds);
        },
        error: () => {
          // Refresh failed — clear spinners so the user isn't stuck and
          // surface the failure. Without this they'd assume the action
          // succeeded while seeing stale row data.
          this.clearRowsUpdating(deviceIds);
          this.snackbarService.showError(
            'Refresh failed — please reload to see updated values.',
            'Close',
            { horizontalPosition: 'center', duration: 5000 }
          );
        },
      });
  }

  private saveForceRefreshToLocalStorage(
    correlationId: string,
    customerId: number
  ): void {
    try {
      const raw = localStorage.getItem(
        DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY
      );
      const pending: Array<{ correlationId: string; customerId: number }> = raw
        ? JSON.parse(raw)
        : [];
      pending.push({ correlationId, customerId });
      localStorage.setItem(
        DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY,
        JSON.stringify(pending)
      );
    } catch {
      // localStorage unavailable — monitoring still works for current session
    }
  }

  private removeForceRefreshFromLocalStorage(correlationId: string): void {
    try {
      const raw = localStorage.getItem(
        DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY
      );
      if (!raw) return;
      const pending: Array<{ correlationId: string; customerId: number }> =
        JSON.parse(raw);
      const filtered = pending.filter((p) => p.correlationId !== correlationId);
      if (filtered.length > 0) {
        localStorage.setItem(
          DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY,
          JSON.stringify(filtered)
        );
      } else {
        localStorage.removeItem(
          DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY
        );
      }
    } catch {
      // Best-effort cleanup
    }
  }

  /**
   * On browser-restart recovery, deviceIds for the original force-refresh
   * are lost (only the correlationId persists). On job completion we can't
   * surgically `applyRowUpdates` the affected rows, so we fall back to a
   * full grid refresh via `refreshGrid()` (alias for `purgeAndRefresh()`).
   */
  private recoverPendingForceRefreshJobs(customerId: number): void {
    try {
      const raw = localStorage.getItem(
        DeviceSearchComponent.FORCE_REFRESH_STORAGE_KEY
      );
      if (!raw) return;
      const pending: Array<{ correlationId: string; customerId: number }> =
        JSON.parse(raw);
      const customerJobs = pending.filter((p) => p.customerId === customerId);

      for (const { correlationId } of customerJobs) {
        // Avoid re-subscribing if already recovered in this session
        if (this.recoveredCorrelationIds.has(correlationId)) continue;
        this.recoveredCorrelationIds.add(correlationId);

        this.jobMonitorService
          .monitorByCorrelationIdWithCallbacks(
            customerId,
            correlationId,
            () => {
              this.removeForceRefreshFromLocalStorage(correlationId);
              this.deviceSearchGrid?.refreshGrid();
            },
            () => {
              this.removeForceRefreshFromLocalStorage(correlationId);
            }
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    } catch {
      // localStorage unavailable — skip recovery
    }
  }

}
